import React, { FC, useEffect, useState , useMemo} from "react";
import { Button, Popover } from "antd";
import { cloneDeep, filter, find, forEach, isEmpty, noop, set, get, sortBy } from "lodash";

import Icon from "@assets/icon_config";
import { Info } from "../../constants";
import { compareArrays, localToUTCFormatDate } from "@utils/helpers";
import CareCoordinationService from "@services/care-coordination";
import NewReminder from "@components/reminder/remindersList/reminder";
import { useCareCoordinationStore } from "@stores/useCareCoordinationStore";
import { useGlobalInformationStore } from "@stores/global_informationStore";
import membersService from "@services/members.service";
import { CASE_STATUS_REASON_REMINDER, MEMBER_DETAILS_CARE_MANAGEMENT_TASKS, USER_ACTION_CARD_PROGRAM, USER_ACTION_DESCRIPTION_REMINDER_DELETE, USER_ACTION_DESCRIPTION_REMINDER_SAVE, USER_ACTION_DESCRIPTION_REMINDER_UPDATE, USER_ACTION_LOCATION_TASK_REMINDER, USER_ACTION_MODULE_MEMBER_DETAIL, USER_ACTION_TAB_CARE_MANAGEMENT } from "@utils/constants";
import { CURRENT_ITEM } from "@components/reminder/constants";
const Remainder: FC<any> = ({ patientId, dependentSectionId, updateParentTask, disabled, taskType, taskId, interventionId, listOfReminders, readonly = false, innerRef, reminderOrigin, disableRecurrence = false, forceUpdateReminder }) => {
  const updatedReminder = useCareCoordinationStore((state) => state.updatedReminder);
  const setUpdatedReminder = useCareCoordinationStore((state) => state.setUpdatedReminder);
  const memberDetailGlobalReminder = useGlobalInformationStore((state) => state.memberDetailGlobalReminder);
  const setMemberDetailGlobalReminder = useGlobalInformationStore((state) => state.setMemberDetailGlobalReminder);

  const [isReminderVisible, setIsReminderVisible] = useState(false);

  const [remindersList, setRemindersList] = useState(listOfReminders);

  // params for update status for Program goals
  const programsData = cloneDeep(useCareCoordinationStore((state) => state.dataForCareCoordination));
  const activeIntervention = find(programsData?.interventions, { id: interventionId });
  const activeTask = find(activeIntervention?.tasks, { id: taskId });
  const currentMember =  membersService?.getCurrentMember;
  const getUserActionParams = (userActionDescription: any, reason: any) => {
    let props = {
      caseStatusReasonTxt: reason,
      actionModule: USER_ACTION_MODULE_MEMBER_DETAIL,
      actionTab: USER_ACTION_TAB_CARE_MANAGEMENT,
      actionCard: `${get(programsData , 'displayName')}$|${get(programsData, "patientProgramId")}$|${USER_ACTION_CARD_PROGRAM}`,
      actionLocation: `${get(activeTask, 'description')}$|${USER_ACTION_LOCATION_TASK_REMINDER}`,
      actionDescription: userActionDescription,
    };
    return props;
  };

  const reminderType = {
    reminderType: MEMBER_DETAILS_CARE_MANAGEMENT_TASKS,
  };

  /* 
    Below useEffect was added to resolve to forceUpdate the reminder List
    when task status was updated to Done, reminder list local state was not getting updated.
    so we added a task level state to observe whether reminder should be forced re-rendered or not
    forceUpdateReminder -> toggles when task status is updated to Done and back to inProgress
  */
  useEffect(() => {
    setRemindersList(listOfReminders)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceUpdateReminder])

  React.useImperativeHandle(innerRef, () => {
    return {
      addReminder: (newReminder: any) => {
        // remindersList.push(newReminder)
        setRemindersList([...remindersList, ...newReminder]);
      },
    };
  });

  const updatedReminderList = useMemo(() => {
    return remindersList
      ?.filter((tempReminder: any) => {
        return !updatedReminder.deletedReminder.includes(Number(tempReminder.reminderId)) && !updatedReminder.deletedReminder.includes(tempReminder.reminderId);
      })
      .map((tempReminder: any) => {
        let copyTempReminder = tempReminder;
        forEach(updatedReminder.editedReminder, (rem) => {
          if (Number(rem.reminderId) === Number(tempReminder.reminderId)) {
            copyTempReminder = { ...rem };
          }
        });
        return copyTempReminder;
      });
  }, [remindersList, updatedReminder.deletedReminder, updatedReminder.editedReminder]); 

  const handleDeleteAction = ({ toBeDeletedItem , loaderCallback , operationRange = CURRENT_ITEM}: any) => {
    CareCoordinationService.deleteReminder({...toBeDeletedItem , patientId , ...dependentSectionId} , operationRange).subscribe({
      next: (res) => {

        const deletedReminders = get(res , 'data.data.deletedReminders' , []);
        const _remindersList = filter(remindersList, (reminder) => {
          return !deletedReminders.includes(reminder?.reminderId);
        });

        setRemindersList(_remindersList);

        //Updating local store for syncing for global reminder drawer
        let copyupdatedReminder = cloneDeep(updatedReminder);
        let updateReminderFlag = false;
        if(compareArrays(get(copyupdatedReminder , 'deletedReminder' ,[]) , deletedReminders)){
          updateReminderFlag = true;
        }
        if (!updateReminderFlag) copyupdatedReminder.deletedReminder.push(...deletedReminders);
        setUpdatedReminder(copyupdatedReminder);

        //Updating Reminders for actionbar to =remove from global store
        setMemberDetailGlobalReminder([...memberDetailGlobalReminder?.filter((rem: any) => !deletedReminders.includes(rem?.reminderId))]);

        loaderCallback();
      },
      error: () => {
        loaderCallback();
      },
    });
    const { caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription } = getUserActionParams(
      USER_ACTION_DESCRIPTION_REMINDER_DELETE,
      CASE_STATUS_REASON_REMINDER
    );
    membersService.updateStatus(currentMember?.patient_id, caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription).subscribe({
      next: (v) => {
        console.log("updated status successfully")
      },
      error: (e) => {
        console.log("update status failed")
      },
    });
  };

  const handleAddAction = ({ toBeAdded }: any, callback = noop) => {
    let reminderCopy = {
      patientId: patientId,
      reminderStartDate: localToUTCFormatDate(toBeAdded?.from),
      reminderEndDate: localToUTCFormatDate(toBeAdded?.to),
      reminder: toBeAdded?.titleVal,
      recurrence: toBeAdded?.recurrence,
      reminderTitle: toBeAdded?.reminderTitle,
      ...dependentSectionId,
    };
    CareCoordinationService.createReminder(reminderCopy).subscribe({
      next: (res) => {
        let response = res.data.data.reminder;

        // const newReminder ={
        //   reminderStartDate: reminderCopy?.fromDate,
        //   reminderEndDate: reminderCopy?.toDate,
        //   reminder: toBeAdded?.titleVal,
        //   reminderId: response?.reminderId,
        // };
        setRemindersList([...remindersList, ...response]);

        callback();

        updateParentTask(response);

        //Updating Reminders for actionbar to =remove from global store
        setMemberDetailGlobalReminder([...memberDetailGlobalReminder, ...response]);
      },
      error: () => {
        callback();
      },
    });
    const { caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription } = getUserActionParams(
      USER_ACTION_DESCRIPTION_REMINDER_SAVE,
      CASE_STATUS_REASON_REMINDER
    );
    membersService.updateStatus(currentMember?.patient_id, caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription).subscribe({
      next: (v) => {
        console.log("updated status successfully")
      },
      error: (e) => {
        console.log("update status failed")
      },
    });
  };

  const handleEditAction = ({ reminder }: any, callback = noop) => {
    let reminderCopy = {
      patientId: patientId,
      reminderStartDate: reminder?.reminderStartDate ? localToUTCFormatDate(reminder?.reminderStartDate) : null,
      reminderEndDate: reminder?.reminderEndDate ? localToUTCFormatDate(reminder?.reminderEndDate) : null,
      reminder: reminder?.reminder,
      reminderId: reminder?.reminderId,
      reminderType: reminderType.reminderType,
      recurrence: reminder?.recurrence,
      reminderStatus: reminder?.reminderStatus,
      prevReminderStartDate: reminder?.prevReminderStartDate,
      parentReminderId: reminder.parentReminderId,
      reminderTitle: reminder?.reminderTitle,
      timeZoneOffset: reminder?.timeZoneOffset,
      ...dependentSectionId,
    };

    CareCoordinationService.editReminder(reminderCopy).subscribe({
      next: (res) => {
        const updatedReminderList = res?.data?.data?.reminder?.updatedReminderList;
        const deletedReminderLIst = res?.data?.data?.reminder?.deactivatedReminders;
        const newReminderList = res?.data?.data?.reminder?.newlyCreatedReminders;

        let _remindersList = cloneDeep(remindersList);
        let newDataForReminders: any[] = [];
        let copyupdatedReminder = cloneDeep(updatedReminder);

        if(deletedReminderLIst && deletedReminderLIst?.length) {
          _remindersList = _remindersList?.filter((item:any) => !find(deletedReminderLIst, {reminderId : item?.reminderId}))
        }

        if(newReminderList && newReminderList?.length) {
          _remindersList = [..._remindersList, ...newReminderList]
        }

        if(updatedReminderList && updatedReminderList?.length) {
          _remindersList = _remindersList.map((item:any) => {
            let temp = cloneDeep(item);
            const findItem = find(updatedReminderList, {reminderId : item?.reminderId})
            if(findItem) {
              temp = {
                ...temp,
                ...findItem
              }
            }
            return temp
          });
        }

        newDataForReminders = sortBy(_remindersList, "reminderStartDate");

        //setUpdatedReminder(copyupdatedReminder);
        setRemindersList(newDataForReminders);
        callback();
      },
      error: () => {
        callback();
      },
    });
    const { caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription } = getUserActionParams(
      USER_ACTION_DESCRIPTION_REMINDER_UPDATE,
      CASE_STATUS_REASON_REMINDER
    );
    membersService.updateStatus(currentMember?.patient_id, caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription).subscribe({
      next: (v) => {
        console.log("updated status successfully")
      },
      error: (e) => {
        console.log("update status failed")
      },
    });
  };

  // const upcomingReminders = filter(remindersList, (rem: any) => moment(utcToLocalFormatDate(rem.reminderEndDate)) >= moment())

  const ReminderIcon = ({reminders} : any) => {
    return (
      <Button
        className="ui-program-intervention-tasks__task__actions__action"
        icon={<Icon name="reminder" colorOfBrand={!isEmpty(reminders) ? "#d15f25" : "#7A869A"} />}
        title="Reminder"
        type="text"
        disabled={taskType === Info}
        onClick={() => setIsReminderVisible(!isReminderVisible)}
      />
    );
  };

  return (
    <>
      {isReminderVisible && <div className="overlay-modal"></div>}
      <NewReminder
            listOfReminders={updatedReminderList}
            disabled={disabled}
            handleDelete={handleDeleteAction}
            handleAdd={handleAddAction}
            handleEdit={handleEditAction}
            closePopOver={() => setIsReminderVisible(!isReminderVisible)}
            readonly={readonly}
            member={{
              patientId: patientId,
              patientName: membersService?.getCurrentMember?.patient_name,
            }}
            reminderOrigin={reminderOrigin}
            disableRecurrence={disableRecurrence}
            isReminderVisible ={isReminderVisible}
            setIsReminderVisible={setIsReminderVisible}
            ReminderIcon={ReminderIcon}
          />
    </>
  );
};

export default Remainder;
